<template>
    <div class="full-height">
        <div
            class="row justify-between items-center bg-primary q-px-md q-py-sm"
        >
            <q-btn-group>
                <q-btn
                    @click="isTableMode = true"
                    :color="isTableMode ? 'primary' : 'white'"
                    :text-color="isTableMode ? 'white' : 'primary'"
                    class="toggle-report-view-button"
                    icon="fas fa-table"
                    size="10px"
                    :outline="!isTableMode"
                >
                    <q-tooltip>
                        {{ $t('Switch to table view') }}
                    </q-tooltip>
                </q-btn>
                <q-btn
                    @click="isTableMode = false"
                    :color="isTableMode ? 'white' : 'primary'"
                    :text-color="isTableMode ? 'primary' : 'white'"
                    class="toggle-report-view-button"
                    icon="fas fa-chart-bar"
                    size="10px"
                    :outline="isTableMode"
                >
                    <q-tooltip>
                        {{ $t('Switch to chart view') }}
                    </q-tooltip>
                </q-btn>
            </q-btn-group>
            <div v-if="isTableMode" class="float-right fit-content">
                <q-checkbox
                    v-model="showColumns.quality"
                    :label="$t('Quality')"
                    class="q-mr-md text-white"
                    dark
                />
                <q-checkbox
                    v-model="showColumns.performance"
                    :label="$t('Performance')"
                    class="q-mr-sm text-white"
                    dark
                />
                <q-checkbox
                    v-model="showColumns.availability"
                    :label="$t('Availability')"
                    class="q-mr-lg text-white"
                    dark
                />
                <q-btn-group flat v-if="!$q.platform.is.mobile">
                    <div>
                        <q-tooltip
                            anchor="top middle"
                            self="bottom middle"
                            :offset="[10, 10]"
                        >
                            {{
                                $t(
                                    'This is a KPI report organized in the same format as the LEAN app, broken down into SKUs with subtotals for rows for assets and a facility total.'
                                )
                            }}
                        </q-tooltip>
                        <q-btn-dropdown
                            id="report-data-dropdown"
                            text-color="white"
                            icon="far fa-file-arrow-down"
                            :label="$t('Report')"
                            class="download-file-buttons"
                            size="12px"
                            flat
                        >
                            <div class="q-pa-md">
                                <q-btn-group spread unelevated>
                                    <q-btn
                                        id="csv-button"
                                        label="CSV"
                                        color="accent"
                                        @click="selectedFileType = 'CSV'"
                                        :outline="selectedFileType !== 'CSV'"
                                    />
                                    <q-btn
                                        id="xls-button"
                                        label="XLS"
                                        color="accent"
                                        @click="selectedFileType = 'XLS'"
                                        :outline="selectedFileType !== 'XLS'"
                                    />
                                </q-btn-group>

                                <div class="q-py-md" style="max-width: 400px">
                                    <q-checkbox
                                        id="subtotals-checkbox"
                                        class=""
                                        v-model="includeSubtotals"
                                        :label="
                                            $t(
                                                'Include subtotals, across all products, including the subtotals per shift'
                                            )
                                        "
                                        color="accent"
                                    />
                                    <q-checkbox
                                        id="shifts-checkbox"
                                        class="full-width q-mt-md"
                                        v-model="includeShifts"
                                        :label="$t('Break down by shift')"
                                        color="accent"
                                    />
                                </div>
                                <div class="row justify-end">
                                    <q-btn
                                        id="cancel-report-button"
                                        @click="selectedFileType = 'CSV'"
                                        :label="$t('Cancel')"
                                        class="q-mr-md"
                                        color="accent"
                                        v-close-popup
                                        outline
                                    />
                                    <q-btn
                                        id="download-report-button"
                                        @click="
                                            exportReportData(selectedFileType)
                                        "
                                        label="Download"
                                        color="accent"
                                        :loading="isDownloadingReportData"
                                    />
                                </div>
                            </div>
                        </q-btn-dropdown>
                    </div>
                </q-btn-group>
            </div>
        </div>

        <!-- Table mode -->
        <performance-report-table
            v-if="isTableMode"
            class="q-mb-md"
            :report="report"
            :visibleColumns="visibleColumns"
            :showColumns="showColumns"
            :label="
                $t('Current Period: {startDate} - {endDate}', {
                    startDate: this.formatDate(report.dateInterval.start),
                    endDate: this.formatDate(report.dateInterval.end)
                })
            "
            :facilityId="facilityId"
        />
        <performance-report-table
            v-if="isTableMode"
            :report="comparisonReport"
            :visibleColumns="visibleColumns"
            :showColumns="showColumns"
            :label="
                $t('Comparison Period: {startDate} - {endDate}', {
                    startDate: this.formatDate(
                        comparisonReport.dateInterval.start
                    ),
                    endDate: this.formatDate(comparisonReport.dateInterval.end)
                })
            "
            :facilityId="facilityId"
        />

        <!-- Dashboard mode -->
        <div v-else class="report-container">
            <performance-dashboard
                :report="report"
                :comparisonReport="comparisonReport"
            />
        </div>
    </div>
</template>

<script>
import { convertToUnitSystem } from '@models/products.js';
import { getLocaleFormat } from '@models/timeformats.js';
import FileExporter from '@utils/FileExporter.js';
import { logFileDownloadMessage } from '@utils/logMessagesForE2ETesting.js';
import { roundTo } from '@utils/numberFormatters.js';
import { mapState } from 'pinia';
import { useAssetsStore } from '@stores/assets.ts';
import { useFacilitiesStore } from '@stores/facilities.ts';
import PerformanceDashboard from './Dashboard/PerformanceDashboard.vue';
import {
    buildPerformanceReport,
    buildTable,
    getReportDataByShift,
    getReportDataFileRows
} from './performanceReport.js';
import PerformanceReportTable from './PerformanceReportTable.vue';
import { useReportsStore } from '@stores/reports.ts';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'ComparisonPerformanceReport',
    components: { PerformanceDashboard, PerformanceReportTable },
    props: {
        records: {
            type: Object,
            required: true
        },
        facilityId: {
            type: Number,
            required: true
        },
        selectedAssets: {
            type: Set,
            required: true
        },
        selectedShifts: {
            type: Set,
            required: true
        },
        shiftIdToNames: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            // column type visibility
            showColumns: {
                availability: false,
                performance: false,
                quality: true
            },

            // button loading animation
            isDownloadingReportData: false,

            // report/table view toggler
            isTableMode: true,
            // report export options
            selectedFileType: 'XLS',
            includeSubtotals: true,
            includeShifts: true
        };
    },
    computed: {
        ...mapState(useAssetsStore, ['getAsset']),
        ...mapState(useFacilitiesStore, ['getFacility']),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange',
            selectedTimezone: 'selectedTimezone'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Build report data of current interval
         @return {Object} report data of current interval
         */
        report() {
            // assign date intervals to corresponding record data
            const { currentReportData } = this.records;
            const report = {
                ...this.generateReport(currentReportData),
                dateInterval: {
                    start: this.getDateRange.start,
                    end: this.getDateRange.end
                }
            };
            return report;
        },
        /**
         * Build report data of comparison interval
         * @return {Object} report data of comparison interval
         */
        comparisonReport() {
            // assign date intervals to corresponding record data
            const { comparisonReportData } = this.records;
            const report = {
                ...this.generateReport(comparisonReportData),
                dateInterval: {
                    start: this.getDateRange.compareStart,
                    end: this.getDateRange.compareEnd
                }
            };
            return report;
        },
        /**
         * All possible filters for this report
         */
        filters() {
            return {
                assetIds: this.selectedAssets,
                shiftIds: this.selectedShifts
            };
        },
        /**
         * Extract facility name from facility id
         */
        facilityName() {
            return this.getFacility(this.facilityId).name;
        },
        /**
         * Get the unit system used by the facility
         * @return {String} unit system
         */
        unitSystem() {
            return this.getFacility(this.facilityId)?.unit_system;
        },
        /**
         * Get the visible columns based on the active column types.
         * @return {String[]}
         */
        visibleColumns() {
            // always show asset name + primary columns
            const visibleColumns = [
                'asset_name',
                'availability',
                'performance',
                'quality',
                'oee',
                'teep'
            ];

            // if column type is set to be visible, show all secondary columns of that column type
            if (this.showColumns.availability) {
                visibleColumns.push(
                    'running_duration',
                    'changeover_duration',
                    'downtime_duration',
                    'availability_downtime_duration',
                    'performance_downtime_duration',
                    'unplanned_downtime',
                    'planned_downtime'
                );
            }
            if (this.showColumns.performance) {
                visibleColumns.push('actual_run_rate', 'planned_rate');
            }
            if (this.showColumns.quality) {
                visibleColumns.push(
                    'total_production_count',
                    'rejected_production_count',
                    'net_production_count',
                    'production_quantity',
                    'rejection_quantity',
                    'net_quantity'
                );
            }
            return visibleColumns;
        },
        /**
         * * Get the file rows for the report data for file exporting
         * @return {Array} file rows for exporting
         */
        reportDataFileRows() {
            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            const compare_start_date = this.formatDate(
                this.getDateRange.compareStart
            );
            const compare_end_date = this.formatDate(
                this.getDateRange.compareEnd
            );

            const currentReportRows = getReportDataFileRows(
                this.report,
                this.unitSystem,
                this.includeSubtotals,
                this.facilityName,
                start_date,
                end_date,
                this.shiftIdToNames
            );

            const comparisonReportRows = getReportDataFileRows(
                this.comparisonReport,
                this.unitSystem,
                this.includeSubtotals,
                this.facilityName,
                compare_start_date,
                compare_end_date,
                this.shiftIdToNames
            );

            // If both reports are present, add an empty row between them for better readability
            if (currentReportRows.length && comparisonReportRows.length) {
                const emptyRow = Array(currentReportRows[0].length).fill('');
                return [
                    ...currentReportRows,
                    emptyRow,
                    ...comparisonReportRows
                ];
            }

            return [...currentReportRows, ...comparisonReportRows];
        },
        /**
         * Get volume unit, based on the unit system
         * @return {String} volume unit
         */
        volumeUnit() {
            return this.unitSystem === 'imperial' ? 'gal' : 'L';
        },
        /**
         * Get weight unit, based on the unit system
         * @return {String} weight unit
         */
        weightUnit() {
            return this.unitSystem === 'imperial' ? 'lb' : 'kg';
        },
        /**
         * Get the report data by shift for file exporting
         * @return {Array} report data by shift for exporting
         */
        reportDataByShift() {
            const { currentReportData, comparisonReportData } = this.records;

            const currentShiftData = buildPerformanceReport(
                currentReportData,
                this.filters
            );

            const currentShiftsReport = getReportDataByShift(
                currentShiftData,
                this.unitSystem,
                this.includeSubtotals,
                this.facilityName,
                this.formatDate(this.getDateRange.start),
                this.formatDate(this.getDateRange.end),
                this.shiftIdToNames
            );

            const comparisonShiftData = buildPerformanceReport(
                comparisonReportData,
                this.filters
            );

            const comparisonShiftsReport = getReportDataByShift(
                comparisonShiftData,
                this.unitSystem,
                this.includeSubtotals,
                this.facilityName,
                this.formatDate(this.getDateRange.compareStart),
                this.formatDate(this.getDateRange.compareEnd),
                this.shiftIdToNames
            );

            // If both reports are present, add an empty row between them for better readability
            if (currentShiftsReport.length && comparisonShiftsReport.length) {
                const emptyRow = Array(currentShiftsReport[0].length).fill('');
                return [
                    ...currentShiftsReport,
                    emptyRow,
                    ...comparisonShiftsReport
                ];
            }

            return [...currentShiftsReport, ...comparisonShiftsReport];
        },
        /**
         * Generates the export file name based on the facility name,
         * date range, and optional flags for shifts and subtotals.
         * @returns {string} The generated file name in the format:
         * "{facilityName}_performance_{shift_}{subtotals_}{start_date}_{end_date}_and_${compare_start_date}_${compare_end_date}"
         */
        getExportFileName() {
            const facilityName = this.facilityName.toLowerCase();
            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            const compare_start_date = this.formatDate(
                this.getDateRange.compareStart
            );
            const compare_end_date = this.formatDate(
                this.getDateRange.compareEnd
            );
            let filename = `${facilityName}_performance_`;
            if (this.includeShifts) {
                filename += 'shift_';
            }

            if (this.includeSubtotals) {
                filename += 'subtotals_';
            }
            return (
                filename +
                `${start_date}_${end_date}_and_${compare_start_date}_${compare_end_date}`
            );
        }
    },
    methods: {
        /**
         * Extract all file rows for a given report data
         * @param {Object} report - report data to extract rows from
         * @param {String} dateStart - start date of the report
         * @param {String} dateEnd - end date of the report
         * @return {Array} 2D array containing file row data
         */
        getFileRows(report, dateStart, dateEnd) {
            let rows = [];

            const filteredRecords = report.filter(record => {
                return (
                    this.filters.assetIds.has(record['asset_id']) &&
                    this.filters.shiftIds.has(record['shift_id'])
                );
            });
            for (const record of filteredRecords) {
                // if the asset is deleted, append (Deleted) to the asset name
                const asset = this.getAsset(record['asset_id']);
                const isDeleted = asset ? asset.is_deleted || false : false;

                // convert unit packing and measure to the facility's unit system
                const {
                    value: converted_unit_packing,
                    unit: converted_unit_measure
                } = convertToUnitSystem(
                    record['unit_packing'] ?? 0,
                    record['unit_measure'] ?? 0,
                    this.unitSystem
                );

                // Calculate the production data
                const production = parseFloat(record['production'] ?? 0);
                const reject_production = parseFloat(
                    record['rejected_production'] ?? 0
                );
                const net_production = production - reject_production;

                const production_quantity = roundTo(
                    production * converted_unit_packing,
                    3
                );
                const reject_quantity = roundTo(
                    reject_production * converted_unit_packing,
                    3
                );
                const net_quantity = roundTo(
                    net_production * converted_unit_packing,
                    3
                );

                let row = [
                    // enclose names in quotes in case they contain commas
                    !isDeleted
                        ? record['asset_name']
                        : this.$t('{assetName} (Deleted)', {
                              assetName: record['asset_name']
                          }),
                    parseFloat(record['quality']),
                    parseFloat(record['availability']),
                    parseFloat(record['availability_downtime_duration']),
                    parseFloat(record['performance']),
                    parseFloat(record['performance_downtime_duration']),
                    parseFloat(record['actual_planned_downtime_duration']),
                    parseFloat(record['changeover_duration']),
                    parseFloat(record['downtime_duration']),
                    parseFloat(record['running_duration']),
                    parseFloat(record['total_duration']),
                    record['product_name'],
                    production,
                    reject_production,
                    net_production,
                    production_quantity,
                    reject_quantity,
                    net_quantity,
                    converted_unit_measure,
                    parseFloat(record['actual_run_rate']),
                    parseFloat(record['planned_rate']),
                    this.shiftIdToNames[record['shift_id']],
                    this.formatDate(dateStart),
                    this.formatDate(dateEnd)
                ];
                rows.push(row);
            }
            return rows;
        },
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Generate table data from report period
         * @param {Object} period
         * @return {Object} table data for specified period
         */
        generateReport(period) {
            const reportData = buildPerformanceReport(period, this.filters);
            const tableData = buildTable(reportData, 'totalDowntime');
            // sort the rows for the array.
            tableData.rows.sort(function (a, b) {
                let firstAssetName = a.name.toLowerCase();
                let secondAssetName = b.name.toLowerCase();
                return firstAssetName.localeCompare(secondAssetName);
            });
            return tableData;
        },
        /**
         * Export aggregated report data to a file
         * @param {String} fileType - file type to export
         */
        exportReportData(fileType) {
            const fileHeaders = [
                this.$t('Facility'),
                this.$t('Asset'),
                this.$t('SKU'),
                this.$t('Product'),
                this.$t('Shift'),
                this.$t('Total production (count)'),
                this.$t('Total quantity ({units})', {
                    units: this.volumeUnit
                }),
                this.$t('Total quantity ({units})', {
                    units: this.weightUnit
                }),
                this.$t('Rejected production (count)'),
                this.$t('Reject quantity ({units})', {
                    units: this.volumeUnit
                }),
                this.$t('Reject quantity ({units})', {
                    units: this.weightUnit
                }),
                this.$t('Net production (count)'),
                this.$t('Net quantity ({units})', {
                    units: this.volumeUnit
                }),
                this.$t('Net quantity ({units})', {
                    units: this.weightUnit
                }),
                this.$t('Quality [%]'),
                this.$t('Actual rate (#/min)'),
                this.$t('Max run rate (#/min)'),
                this.$t('Performance [%]'),
                this.$t('Running (min)'),
                this.$t('Switch (min)'),
                this.$t('Total downtime (min)'),
                this.$t('Availability losses (min)'),
                this.$t('Performance losses (min)'),
                this.$t('Unplanned downtime (min)'),
                this.$t('Planned downtime (min)'),
                this.$t('Availability [%]'),
                this.$t('OEE [%]'),
                this.$t('Report Start Date').concat(
                    ` [${this.selectedTimezone}]`
                ),
                this.$t('Report End Date').concat(` [${this.selectedTimezone}]`)
            ];

            // Begin loading
            this.isDownloadingReportData = true;
            try {
                const fileExporter = new FileExporter({
                    headers: fileHeaders
                });

                if (fileType === 'CSV') {
                    fileExporter.exportCSV({
                        filename: this.getExportFileName,
                        rows: this.includeShifts
                            ? this.reportDataByShift
                            : this.reportDataFileRows
                    });
                } else if (fileType === 'XLS') {
                    fileExporter.exportXLS({
                        filename: this.getExportFileName,
                        sheetName: 'Performance Report',
                        rows: this.includeShifts
                            ? this.reportDataByShift
                            : this.reportDataFileRows
                    });
                }
                // log message for E2E testing
                // The report filter is a string that contains the options selected for the report
                const reportFilter = [
                    this.includeShifts && 'Shifts',
                    this.includeSubtotals && 'Subtotals'
                ]
                    .filter(Boolean)
                    .join('_');
                logFileDownloadMessage(fileType, reportFilter);
            } catch (error) {
                console.error(error);
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }

            // End loading
            this.isDownloadingReportData = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.toggle-report-view-button :deep(.q-btn__wrapper) {
    padding: 6px;
}
</style>
